/**
 * Promotion Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { promotionService } from '../services/promotionService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  CreatePromotionPayload,
  UpdatePromotionPayload,
  GetPromotionsParams,
} from '../types/promotion.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get promotions list hook
 */
export function useGetPromotions(
  params?: GetPromotionsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.promotion,
      params?.page || 1,
      params?.limit || 10,
      params?.search || '',
      params?.status,
    ],
    queryFn: () => promotionService.getPromotions(params),
    ...options,
  });
}

/**
 * Get single promotion by ID hook
 */
export function useGetPromotion(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.promotion, id],
    queryFn: () => promotionService.getPromotion(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create promotion hook
 */
export function useCreatePromotion(
  options?: UseMutationOptions<any, any, CreatePromotionPayload, any>
) {
  return useApiMutation<any, any, CreatePromotionPayload, any>({
    mutationFn: ((data: CreatePromotionPayload) => promotionService.createPromotion(data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.promotion] });
    },
    ...options,
  });
}

/**
 * Update promotion hook
 */
export function useUpdatePromotion(
  options?: UseMutationOptions<any, any, { id: string; data: UpdatePromotionPayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdatePromotionPayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdatePromotionPayload }) =>
      promotionService.updatePromotion(variables.id, variables.data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.promotion] });
    },
    ...options,
  });
}

/**
 * Delete promotion hook
 */
export function useDeletePromotion(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => promotionService.deletePromotion(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.promotion] });
    },
    ...options,
  });
}
